import { X } from "lucide-react";
import { useBanner } from "@/hooks/use-banner";
import { PLUGIN_VERSION } from "@/helpers/plugin";

const BOOK_CALL_URL =
  "https://www.webyes.com/free-accessibility-consulting/" +
  "?utm_source=accessibility_toolkit" +
  "&utm_medium=wordpress" +
  "&utm_campaign=consultation" +
  `&utm_content=${PLUGIN_VERSION}`;

const StarSVG = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 35 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M16.8073 3.34686C16.8712 3.21774 16.9699 3.10906 17.0923 3.03307C17.2147 2.95708 17.3559 2.91681 17.5 2.91681C17.6441 2.91681 17.7853 2.95708 17.9077 3.03307C18.0301 3.10906 18.1288 3.21774 18.1927 3.34686L21.5615 10.1704C21.7834 10.6195 22.111 11.0081 22.5161 11.3027C22.9213 11.5974 23.3919 11.7893 23.8875 11.8621L31.4213 12.9646C31.564 12.9853 31.6981 13.0455 31.8084 13.1384C31.9188 13.2313 32.0009 13.3533 32.0455 13.4905C32.0901 13.6276 32.0954 13.7745 32.0609 13.9146C32.0263 14.0546 31.9533 14.1822 31.85 14.2829L26.4017 19.5883C26.0424 19.9385 25.7735 20.3707 25.6183 20.8478C25.4631 21.3249 25.4261 21.8325 25.5106 22.3271L26.7969 29.8229C26.8221 29.9656 26.8067 30.1125 26.7524 30.2468C26.6981 30.3812 26.6072 30.4975 26.49 30.5827C26.3727 30.6678 26.2339 30.7183 26.0894 30.7284C25.9449 30.7384 25.8004 30.7077 25.6725 30.6396L18.9379 27.0987C18.4942 26.8657 18.0005 26.744 17.4993 26.744C16.9981 26.744 16.5044 26.8657 16.0606 27.0987L9.32752 30.6396C9.19967 30.7073 9.05539 30.7377 8.91109 30.7274C8.7668 30.7172 8.62828 30.6666 8.51128 30.5816C8.39429 30.4965 8.30353 30.3803 8.24931 30.2461C8.19509 30.112 8.1796 29.9654 8.2046 29.8229L9.48939 22.3285C9.57426 21.8338 9.5375 21.3258 9.38226 20.8484C9.22703 20.371 8.95799 19.9385 8.59835 19.5883L3.15002 14.2844C3.04589 14.1838 2.97209 14.056 2.93705 13.9155C2.902 13.775 2.90712 13.6276 2.9518 13.4898C2.99649 13.3521 3.07895 13.2298 3.1898 13.1366C3.30065 13.0435 3.43542 12.9834 3.57877 12.9631L11.1111 11.8621C11.6073 11.7899 12.0785 11.5982 12.4842 11.3035C12.8899 11.0088 13.2179 10.62 13.44 10.1704L16.8073 3.34686Z"
      fill="#FFB700"
    />
  </svg>
);

const AccessibilityIcon = () => (
  <svg
    width="12"
    height="14"
    viewBox="0 0 14 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="7" cy="2.5" r="2" fill="white" />
    <path
      d="M3.5 5.5L7 6.5L10.5 5.5M7 6.5V10M5 14L7 10L9 14"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ConsultationBanner = () => {
  const { isVisible, handleDismiss } = useBanner({
    slug: "consultation-banner",
    defaultState: {
      dismissed: false,
      dismissedAt: null,
    },
  });

  if (!isVisible) return null;

  return (
    <div className="cy:mb-6">
      <div className="cy:min-h-[112px] cy:relative cy:bg-gradient-to-br cy:from-blue-600 cy:to-sky-500 cy:overflow-hidden cy:rounded-xl cy:flex cy:items-center cy:pl-[267px] cy:pr-10 cy:py-4">
        {/* Decorative blur (top-right) */}
        <div
          aria-hidden="true"
          className="cy:w-12 cy:h-48 cy:absolute cy:origin-top-left cy:bg-blue-800 cy:blur-[50px]"
          style={{
            left: "calc(100% - 115px)",
            top: "-52.16px",
            transform: "rotate(-50.18deg)",
            boxShadow: "-24px 4px 28px 0px rgba(0,0,0,0.25)",
          }}
        />

        {/* Card mockup */}
        <div
          aria-hidden="true"
          className="cy:w-28 cy:h-32 cy:left-[30px] cy:top-[15px] cy:absolute cy:bg-white cy:rounded-2xl cy:outline cy:outline-2 cy:outline-white/30 cy:overflow-hidden"
        >
          <div
            className="cy:w-12 cy:h-10 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 59, top: 17.29 }}
          />
          <div
            className="cy:w-24 cy:h-5 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 8.81, top: 74.17 }}
          />
          <div
            className="cy:w-8 cy:h-6 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 8.81, top: 88.06 }}
          />
          <div
            className="cy:w-8 cy:h-6 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 43.15, top: 88.06 }}
          />
          <div
            className="cy:w-8 cy:h-6 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 77.49, top: 88.06 }}
          />
          <div
            className="cy:w-11 cy:h-1.5 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 8.81, top: 23.02 }}
          />
          <div
            className="cy:w-4 cy:h-1.5 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 8.81, top: 47.67 }}
          />
          <div
            className="cy:w-4 cy:h-1.5 cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ left: 27.74, top: 47.67 }}
          />
          <div
            className="cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ width: 40, height: 2.64, left: 8.81, top: 32.26 }}
          />
          <div
            className="cy:absolute cy:bg-indigo-100 cy:rounded-sm"
            style={{ width: 28, height: 2.64, left: 8.81, top: 37.55 }}
          />
        </div>

        {/* "Accessibility Expert" pill */}
        <div
          aria-hidden="true"
          className="cy:px-2.5 cy:py-1.5 cy:left-[70px] cy:top-[32px] cy:absolute cy:bg-white cy:rounded-xl cy:inline-flex cy:justify-start cy:items-center cy:gap-2"
          style={{ boxShadow: "0px 16px 24px 0px rgba(33,15,111,0.37)" }}
        >
          <div className="cy:w-6 cy:h-6 cy:bg-[#0B66E4] cy:rounded-full cy:flex cy:items-center cy:justify-center cy:shrink-0">
            <AccessibilityIcon />
          </div>
          <span className="cy:text-zinc-800 cy:text-xs cy:font-medium cy:leading-4 cy:whitespace-nowrap">
            Accessibility Expert
          </span>
        </div>

        {/* Content */}
        <div className="cy:flex cy:flex-1 cy:items-center cy:justify-between cy:gap-4 cy:min-w-0">
          <div className="cy:flex cy:flex-col cy:justify-center cy:items-start cy:gap-1.5 cy:min-w-0">
            <div className="cy:text-xl cy:font-bold cy:leading-8 cy:text-white">
              You've unlocked a free 45-min accessibility consultation!
            </div>
            <div>
              <span className="cy:text-white cy:text-sm cy:font-normal cy:leading-5">
                Get a live site review + a clear, prioritised plan for{" "}
              </span>
              <span className="cy:text-white cy:text-sm cy:font-bold cy:leading-5">
                WCAG 2.2, ADA &amp; EAA.
              </span>
            </div>
          </div>

          <div className="cy:shrink-0">
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cy:px-9 cy:py-3 cy:bg-white cy:hover:bg-white/90 cy:rounded cy:inline-flex cy:justify-center cy:items-center cy:gap-2.5 cy:text-[#0B66E4]! cy:text-base cy:font-bold! cy:leading-6 cy:no-underline"
            >
              Book a call
              <span className="cy:sr-only">(opens in a new tab)</span>
            </a>
          </div>
        </div>

        {/* Star — rendered last so it sits above the card */}
        <div className="cy:w-9 cy:h-9 cy:left-[12px] cy:top-[7px] cy:absolute cy:overflow-hidden cy:flex cy:items-center cy:justify-center cy:z-20">
          <StarSVG />
        </div>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="cy:w-6 cy:h-6 cy:absolute cy:top-[9px] cy:right-[9px] cy:z-50 cy:flex cy:items-center cy:justify-center cy:text-white/70 cy:hover:text-white cy:transition-colors cy:rounded-full cy:hover:bg-white/10 cy:bg-transparent cy:border-0 cy:cursor-pointer"
          aria-label="Dismiss banner"
          type="button"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
